function padDatePart(value) {
  return String(value).padStart(2, "0");
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function normalizeEndOfDayLoadDateValue(value = "") {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return [
      value.getFullYear(),
      padDatePart(value.getMonth() + 1),
      padDatePart(value.getDate()),
    ].join("-");
  }
  return typeof value === "string" ? value.trim().slice(0, 10) : "";
}

function parseEndOfDayLoadDateValue(value = "") {
  const normalizedValue = normalizeEndOfDayLoadDateValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcTime = Date.UTC(year, month - 1, day);
  const parsedDate = new Date(utcTime);
  if (
    parsedDate.getUTCFullYear() !== year
    || parsedDate.getUTCMonth() + 1 !== month
    || parsedDate.getUTCDate() !== day
  ) {
    return null;
  }

  return { day, month, utcTime, year };
}

// « Demain » se compte depuis la date du chargement PM (date du dispatch source), pas depuis la date de l'ordinateur.
function resolveTodayDateValue(todayDate = "", sourceDispatchDate = "") {
  return normalizeEndOfDayLoadDateValue(todayDate)
    || normalizeEndOfDayLoadDateValue(sourceDispatchDate)
    || normalizeEndOfDayLoadDateValue(new Date());
}

function isTargetTomorrow(targetDispatchDate = "", todayDate = "", sourceDispatchDate = "") {
  const targetDate = parseEndOfDayLoadDateValue(targetDispatchDate);
  const currentDate = parseEndOfDayLoadDateValue(resolveTodayDateValue(todayDate, sourceDispatchDate));
  if (!targetDate || !currentDate) {
    return false;
  }

  return Math.round((targetDate.utcTime - currentDate.utcTime) / DAY_MS) === 1;
}

function formatEndOfDayLoadTargetDateLabel(targetDispatchDate = "", {
  abbreviateMonth = false,
} = {}) {
  const parsedDate = parseEndOfDayLoadDateValue(targetDispatchDate);
  if (!parsedDate) {
    return "";
  }

  const targetDate = new Date(parsedDate.utcTime);
  if (abbreviateMonth) {
    return new Intl.DateTimeFormat("fr-CA", {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(targetDate);
  }

  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(targetDate);
}

function getNextDayModeShortLabel({
  sourceDispatchDate = "",
  targetDispatchDate = "",
  todayDate = "",
} = {}) {
  const normalizedTargetDate = normalizeEndOfDayLoadDateValue(targetDispatchDate);
  if (!normalizedTargetDate || isTargetTomorrow(normalizedTargetDate, todayDate, sourceDispatchDate)) {
    return "Demain";
  }

  return formatEndOfDayLoadTargetDateLabel(normalizedTargetDate, { abbreviateMonth: true }) || "Demain";
}

export function normalizeEndOfDayLoadModeForDisplay(visualMode = "next_day") {
  const normalizedValue = typeof visualMode === "string" ? visualMode.trim().toLowerCase() : "";
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "next_day";
}

export function getEndOfDayLoadModeShortLabel(visualMode = "next_day", {
  sourceDispatchDate = "",
  targetDispatchDate = "",
  todayDate = "",
} = {}) {
  const normalizedMode = normalizeEndOfDayLoadModeForDisplay(visualMode);
  if (normalizedMode === "evening") {
    return "Soir";
  }

  return getNextDayModeShortLabel({ sourceDispatchDate, targetDispatchDate, todayDate });
}

export function getEndOfDayLoadModeLongLabel(visualMode = "next_day", {
  sourceDispatchDate = "",
  targetDispatchDate = "",
  todayDate = "",
} = {}) {
  const normalizedMode = normalizeEndOfDayLoadModeForDisplay(visualMode);
  if (normalizedMode === "evening") {
    return "Livrer soir";
  }

  const normalizedTargetDate = normalizeEndOfDayLoadDateValue(targetDispatchDate);
  if (!normalizedTargetDate || isTargetTomorrow(normalizedTargetDate, todayDate, sourceDispatchDate)) {
    return "Livrer demain";
  }

  const dateLabel = formatEndOfDayLoadTargetDateLabel(normalizedTargetDate);
  return dateLabel ? `Livrer le ${dateLabel}` : "Livrer demain";
}

export function getEndOfDayLoadModeIconName(visualMode = "next_day") {
  return normalizeEndOfDayLoadModeForDisplay(visualMode) === "evening" ? "moon" : "sun";
}
